import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Switch from '@mui/material/Switch';
import BusinessIcon from '@mui/icons-material/Business';
import Draggable from 'react-draggable';
import Paper from '@mui/material/Paper';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getClients } from '../../../store/slices/clientsSlice';
import { PUT, PATCH } from '../../../configs/api';
import { api } from '../../../utils/constants';
import { updateUser } from '../../../store/slices/usersSlice';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  ASSIGNABLE_ROLES,
  formatRoleLabel,
  hasRole,
  isManagement,
  isOwner,
  ROLES,
} from '../../../utils/permissions';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Badge from '@mui/material/Badge';
import ActivityTab from './tabs/ActivityTab';
import UserAvatar from '../../../components/UserAvatar';

function PaperComponent(props) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

// Phone formatting function
const formatPhoneNumber = (value) => {
  // Remove all non-numeric characters
  const phoneNumber = value.replace(/\D/g, '');
  
  // Format as (XXX) XXX-XXXX
  if (phoneNumber.length >= 6) {
    const areaCode = phoneNumber.substring(0, 3);
    const firstPart = phoneNumber.substring(3, 6);
    const secondPart = phoneNumber.substring(6, 10);
    return `(${areaCode}) ${firstPart}-${secondPart}`;
  } else if (phoneNumber.length >= 3) {
    const areaCode = phoneNumber.substring(0, 3);
    const firstPart = phoneNumber.substring(3);
    return `(${areaCode}) ${firstPart}`;
  } else if (phoneNumber.length > 0) {
    return `(${phoneNumber}`;
  }
  return phoneNumber;
};

const emptyUser = {
  id: '',
  first_name: '',
  last_name: '',
  email: '',
  username: '',
  phone: '',
  avatar_s3_key: null,
  roles: ['admin'],
};

// Helper function to get display name for users (admin/dispatcher)
const getDisplayName = (user) => {
  return `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
};

// Clients this user (admin) has access to, from Redux clients.admins
function useClientAccessForUser(userId, roles) {
  const clientsData = useSelector(getClients);
  return useMemo(() => {
    const isAdminUser = Array.isArray(roles) && roles.includes(ROLES.ADMIN);
    if (!isAdminUser || !userId) return [];
    const list = clientsData?.clients || [];
    return list.filter((c) => (c.admins || []).some((a) => (typeof a === 'object' ? a.id : a) === userId));
  }, [clientsData?.clients, userId, roles]);
}

export default function UserDetailsDialog({ selectedUser, setSelectedUser, user }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const clientAccess = useClientAccessForUser(selectedUser?.id, selectedUser?.roles);
  const [editUser, setEditUser] = useState(emptyUser);
  const [editMode, setEditMode] = useState(false);
  const [errors, setErrors] = useState({
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    phone: '',
    roles: '',
  });
  const [showError, setShowError] = useState('');
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [activitiesCount, setActivitiesCount] = useState(0);
  const [activityRefreshToken, setActivityRefreshToken] = useState(0);

  const canManageUsers = isManagement(user);
  const isSelectedOwner = hasRole(selectedUser, ROLES.OWNER);
  const canManageSelectedUser = canManageUsers && (!isSelectedOwner || isOwner(user));
  const canToggleStatus = canManageSelectedUser
    && selectedUser?.id
    && selectedUser.id !== user?.id
    && !isSelectedOwner;

  // Clear form errors
  useEffect(() => {
    setErrors({
      first_name: '',
      last_name: '',
      email: '',
      username: '',
      phone: '',
      roles: '',
    });
    setShowError('');
  }, [editUser]);

  useEffect(() => {
    setEditUser(selectedUser ? { ...selectedUser } : emptyUser);
    setIsActive(selectedUser?.is_active !== false);
    setEditMode(false);
    setActiveTab(0);
    setActivitiesCount(0);
    setActivityRefreshToken(0);
  }, [selectedUser]);

  if (!selectedUser) return null;

  const handleUpdateUser = async () => {
    setShowError('');
    setLoading(true);
    setErrors({
      first_name: '',
      last_name: '',
      email: '',
      username: '',
      phone: '',
      roles: '',
    });

    let newErrors = {};
    
    // Name validation (required for admin/dispatcher)
    (editUser.first_name?.length < 2 || editUser.first_name?.length > 50) && (newErrors.first_name = 'First name must be between 2 and 50 characters');
    (editUser.last_name?.length < 2 || editUser.last_name?.length > 50) && (newErrors.last_name = 'Last name must be between 2 and 50 characters');
    
    // Email validation (required)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    (!emailRegex.test(editUser.email)) && (newErrors.email = 'Please enter a valid email address');
    
    // Username validation (required)
    const usernameRegex = /^[a-zA-Z0-9_]+$/;
    if (editUser.username?.length < 3 || editUser.username?.length > 50) {
      newErrors.username = 'Username must be between 3 and 50 characters';
    } else if (editUser.username && !usernameRegex.test(editUser.username)) {
      newErrors.username = 'Username must contain only alphanumeric characters and underscores';
    }
    
    // Phone validation (optional, but if provided must be valid)
    if (editUser.phone) {
      const phoneDigits = editUser.phone.replace(/\D/g, '');
      (phoneDigits.length < 10 || phoneDigits.length > 10) && (newErrors.phone = 'Please enter a valid 10-digit US phone number');
    }

    if (!Array.isArray(editUser.roles) || editUser.roles.length === 0) {
      newErrors.roles = 'At least one role is required';
    }

    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      const userData = { ...editUser };
      
      const { status, data, message } = await PUT(api.updateUser, {}, userData);
      if (status === 200) {
        setEditMode(false);
        setShowSuccessToast(true);
        dispatch(updateUser(data));
        setSelectedUser(data);
        setActivityRefreshToken((t) => t + 1);
      } else if (status === 409) {
        setShowError(message || 'A user with this email already exists.');
      } else {
        setShowError(data?.message || 'Something went wrong, please try again later.');
      }
    }
    setLoading(false);
  };

  const handleToggleActiveStatus = async (nextActive) => {
    if (!canToggleStatus || statusLoading) return;

    const previousActive = isActive;
    setShowError('');
    setIsActive(nextActive);
    setSelectedUser({ ...selectedUser, is_active: nextActive });
    setEditUser((prev) => ({ ...prev, is_active: nextActive }));
    setStatusLoading(true);

    try {
      const { status, data, message } = await PATCH(api.updateUserStatus(selectedUser.id), {}, { is_active: nextActive });
      if (status === 200) {
        dispatch(updateUser(data));
        setSelectedUser(data);
        setEditUser((prev) => ({ ...prev, ...data }));
        setIsActive(data.is_active !== false);
        setShowSuccessToast(true);
        setActivityRefreshToken((t) => t + 1);
      } else {
        setIsActive(previousActive);
        setSelectedUser({ ...selectedUser, is_active: previousActive });
        setEditUser((prev) => ({ ...prev, is_active: previousActive }));
        setShowError(data?.message || message || 'Failed to update user status.');
      }
    } catch {
      setIsActive(previousActive);
      setSelectedUser({ ...selectedUser, is_active: previousActive });
      setEditUser((prev) => ({ ...prev, is_active: previousActive }));
      setShowError('Failed to update user status.');
    }
    setStatusLoading(false);
  };

  return (
    <Dialog
      open={!!selectedUser}
      onClose={() => {
        setSelectedUser(null); 
        setEditMode(false); 
      }}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallScreen}
      PaperComponent={isSmallScreen ? undefined : PaperComponent}
      aria-labelledby="draggable-dialog-title"
      slotProps={{
        paper: {
          sx: {
            borderRadius: isSmallScreen ? 0 : 2,
            width: isSmallScreen ? '100%' : '40vw',
            maxWidth: isSmallScreen ? '100%' : 'none',
            maxHeight: isSmallScreen ? '100%' : '90vh',
            m: isSmallScreen ? 0 : undefined,
          }
        }
      }}
    >
      {/* Header */}
      <Box 
        id="draggable-dialog-title"
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          p: 3,
          backgroundColor: 'background.paper',
          cursor: 'move'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <UserAvatar
            user={editUser}
            size={44}
            sx={{
              background: isDarkMode
                ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                : 'linear-gradient(135deg, #000000 0%, #333333 100%)',
              color: 'white',
            }}
          />
          <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            color: 'text.primary',
            fontSize: '1.25rem',
            fontFamily: 'inherit'
          }}>
            {getDisplayName(editUser)}
          </Typography>
          <Chip
            size="small"
            label={isActive ? 'Active' : 'Inactive'}
            color={isActive ? 'success' : 'default'}
            variant={isActive ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600 }}
          />
        </Box>
        <IconButton 
          onClick={() => {
            setSelectedUser(null); 
            setEditMode(false); 
          }} 
          sx={{ 
            color: 'text.secondary',
            padding: '4px',
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {canManageSelectedUser && (
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{
            px: 3,
            borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
            minHeight: 44,
            overflow: 'visible',
            '& .MuiTabs-scroller': { overflow: 'visible !important' },
            '& .MuiTabs-flexContainer': { overflow: 'visible' },
          }}
        >
          <Tab label="Details" sx={{ textTransform: 'none', minHeight: 44 }} />
          <Tab
            label={
              <Badge
                badgeContent={activitiesCount}
                color="primary"
                max={999}
                sx={{
                  '& .MuiBadge-badge': {
                    right: -6,
                    top: 2,
                    fontSize: '0.7rem',
                    minWidth: '18px',
                    height: '18px',
                    padding: '0 4px',
                  },
                }}
              >
                Activity
              </Badge>
            }
            sx={{ textTransform: 'none', minHeight: 44, pr: 2.5, overflow: 'visible' }}
          />
        </Tabs>
      )}

      <DialogContent sx={{ p: 3, position: 'relative' }}>
        <Box
          sx={{
            visibility: activeTab === 0 ? 'visible' : 'hidden',
            pointerEvents: activeTab === 0 ? 'auto' : 'none',
          }}
        >
        {/* Error Message */}
        {showError && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 600 }}>
              {showError}
            </Typography>
          </Box>
        )}

        {/* Form Fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* First Name */}
          <TextField 
            fullWidth 
            label="First Name" 
            variant="outlined" 
            slotProps={{ input: { readOnly: !editMode, }, }} 
            value={editUser.first_name || ''}
            error={!!errors.first_name}
            helperText={errors.first_name}
            onChange={(event) => setEditUser({ ...editUser, first_name: event.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          {/* Last Name */}
          <TextField 
            fullWidth 
            label="Last Name" 
            variant="outlined" 
            slotProps={{ input: { readOnly: !editMode, }, }} 
            value={editUser.last_name || ''}
            error={!!errors.last_name}
            helperText={errors.last_name}
            onChange={(event) => setEditUser({ ...editUser, last_name: event.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          {/* Email */}
          <TextField 
            fullWidth 
            label="Email" 
            variant="outlined" 
            type="email"
            slotProps={{ input: { readOnly: !editMode, }, }} 
            value={editUser.email || ''}
            error={!!errors.email}
            helperText={errors.email}
            onChange={(event) => setEditUser({ ...editUser, email: event.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          {/* Username */}
          <TextField 
            fullWidth 
            label="Username" 
            variant="outlined" 
            slotProps={{ input: { readOnly: !editMode, }, }} 
            value={editUser.username || ''}
            error={!!errors.username}
            helperText={errors.username || '3-50 characters, alphanumeric and underscores only'}
            onChange={(event) => setEditUser({ ...editUser, username: event.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          {/* Roles (owners/GM for non-owner accounts) */}
          <FormControl fullWidth variant="outlined" error={!!errors.roles}>
            <InputLabel sx={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'text.secondary',
              '&.Mui-focused': {
                color: isDarkMode ? '#fff' : '#000',
              }
            }}>Roles</InputLabel>
            <Select
              multiple
              value={Array.isArray(editUser.roles) ? editUser.roles : []}
              label="Roles"
              onChange={(event) => {
                const value = event.target.value;
                setEditUser({ ...editUser, roles: typeof value === 'string' ? value.split(',') : value });
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={formatRoleLabel(value)} size="small" sx={{ height: 22, fontSize: '0.75rem' }} />
                  ))}
                </Box>
              )}
              sx={{
                borderRadius: 1,
                backgroundColor: 'background.paper',
                fontSize: '0.875rem',
                color: 'text.primary',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              }}
              disabled={!editMode || !canManageSelectedUser}
            >
              {ASSIGNABLE_ROLES.map((role) => (
                <MenuItem key={role} value={role} sx={{ fontSize: '0.875rem' }}>
                  <Checkbox checked={(editUser.roles || []).includes(role)} size="small" />
                  <ListItemText primary={formatRoleLabel(role)} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </MenuItem>
              ))}
            </Select>
            {errors.roles && (
              <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, ml: 1.5, fontSize: '0.75rem' }}>
                {errors.roles}
              </Typography>
            )}
          </FormControl>

          {canToggleStatus && (
            <Box sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: isActive ? (isDarkMode ? '#1e293b' : '#f5f5f5') : (isDarkMode ? '#334155' : '#f5f5f5'),
              border: isActive ? (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0') : '1px solid #9e9e9e',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary', mb: 0.5 }}>
                    Company Access
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    {isActive
                      ? 'This user can access this company and be assigned work here'
                      : 'This user cannot access this company. If they have no other active companies, they also cannot log in'}
                  </Typography>
                </Box>
                <Switch
                  checked={isActive}
                  disabled={statusLoading || editMode}
                  onChange={(event) => handleToggleActiveStatus(event.target.checked)}
                  color="success"
                />
              </Box>
            </Box>
          )}

          {/* Client access (owners only, when viewing an admin) */}
          {canManageUsers && hasRole(selectedUser, ROLES.ADMIN) && (
            <Box>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 1.5,
                }}
              >
                Client access
              </Typography>
              {clientAccess.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  No client access
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {clientAccess.map((client) => (
                    <Chip
                      key={client.id}
                      icon={<BusinessIcon sx={{ fontSize: '1rem' }} />}
                      label={client.client_name || client.display_as}
                      size="small"
                      sx={{
                        borderRadius: 1,
                        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                        color: 'text.primary',
                        fontWeight: 500,
                        fontSize: '0.8125rem',
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Phone (Optional) */}
          <TextField 
            fullWidth 
            label="Phone (Optional)" 
            value={editUser.phone || ''} 
            variant="outlined" 
            onChange={(event) => {
              const formattedPhone = formatPhoneNumber(event.target.value);
              setEditUser({ ...editUser, phone: formattedPhone });
            }}
            error={!!errors.phone}
            helperText={errors.phone}
            InputProps={{
              readOnly: !editMode,
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '1.2rem' }}>🇺🇸</span>
                    <Typography sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>
                      +1
                    </Typography>
                  </Box>
                </InputAdornment>
              ),
            }}
            placeholder="(555) 123-4567"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          {/* Created At (Read Only) */}
          <TextField 
            fullWidth 
            label="Created At" 
            value={editUser.created_at ? new Date(editUser.created_at).toLocaleDateString() : ''} 
            variant="outlined" 
            slotProps={{ input: { readOnly: true, }, }} 
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          {/* Updated At (Read Only) */}
          <TextField 
            fullWidth 
            label="Updated At" 
            value={editUser.updated_at ? new Date(editUser.updated_at).toLocaleDateString() : ''} 
            variant="outlined" 
            slotProps={{ input: { readOnly: true, }, }} 
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />
        </Box>
        </Box>

        {canManageSelectedUser && (
          <Box
            sx={{
              display: activeTab === 1 ? 'block' : 'none',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              p: 3,
              overflow: 'hidden',
            }}
          >
            <ActivityTab
              userId={selectedUser.id}
              refreshToken={activityRefreshToken}
              onActivitiesCountChange={setActivitiesCount}
            />
          </Box>
        )}
      </DialogContent>

      {/* Dialog Actions — keep mounted so Activity tab matches Details height */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          p: 3,
          borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
          visibility: activeTab === 0 ? 'visible' : 'hidden',
          pointerEvents: activeTab === 0 ? 'auto' : 'none',
        }}
      >
        {showError && (
          <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 600, mr: 'auto' }}>
            {showError}
          </Typography>
        )}
        
        {editMode ? (
          <>
            <Button 
              variant="outlined"
              onClick={() => {
                setEditUser(selectedUser);
                setEditMode(false);
              }}
              sx={{ 
                borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 600,
                px: 3,
                py: 1.25,
                borderRadius: 1,
                '&:hover': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                  backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                }
              }}
            >
              Cancel
            </Button>
            
            <Button 
              disabled={loading} 
              variant="contained" 
              onClick={handleUpdateUser}
              sx={{ 
                backgroundColor: isDarkMode ? '#fff' : '#000',
                color: isDarkMode ? '#000' : 'white',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 600,
                px: 3,
                py: 1.25,
                borderRadius: 1,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                  boxShadow: 'none',
                },
                '&:disabled': {
                  backgroundColor: isDarkMode ? '#475569' : '#666',
                  color: isDarkMode ? '#94a3b8' : '#fff',
                }
              }}
            >
              Save Changes
            </Button>
          </>
        ) : (
          canManageSelectedUser && (
            <Button
              variant="contained" 
              onClick={() => {
                setEditMode(true);
              }}
              sx={{ 
                backgroundColor: isDarkMode ? '#fff' : '#000',
                color: isDarkMode ? '#000' : 'white',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 600,
                px: 3,
                py: 1.25,
                borderRadius: 1,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                  boxShadow: 'none',
                }
              }}
            >
              Edit
            </Button>
          )
        )}
      </Box>

      <Snackbar
        open={showSuccessToast}
        autoHideDuration={3000}
        onClose={() => setShowSuccessToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessToast(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          User updated successfully
        </Alert>
      </Snackbar>
    </Dialog>
  )
}
